import { createSlice } from '@reduxjs/toolkit'
import {
  AggregateTransaction,
  Bank,
  BankBranch,
  Company,
  CompanyAttributes,
  ClientDetail,
  CsvImportSetting,
  DetailPropConfig,
  TransactionPropConfig,
  CompanyEvaluation,
  CompanyRepresentative,
  BranchOffice,
  VisitorRegistrationForm,
  VisitorRegistrationFormWorker,
  VisitorRegistrationFormWorkerFields,
  VisitorRegistrationFormSubcontractor,
  VisitorRegistrationFormSubcontractorFields,
  VisitorRegistrationRecordSet,
  VisitorRegistrationAnswers,
  VisitorRegistrationCompanyFields,
  CompanyAgreementSettings,
} from '../../types/Company'
import { createAsyncAction } from '../actions'
import { RelatedUser, Role, User } from '../../types/User'
import { ApprovalFlow } from '../../types/ApprovalFlow'
import { convertMembershipFee, MembershipFee2, Moneyspace } from '../../types/Moneyspace'
import { JobActions } from '../job'
import { DashboardActions } from '../dashboard'
import { ProjectDetail } from '../../types/construction/project'
import { TransactionActions } from '../transactions'

export type CompanyState = {
  company?: Company
  clients: Company[]
  clientDetails?: ClientDetail[]
  users: User[]
  bankSearchResults: Bank[]
  bankBranchSearchResults: BankBranch[]
  inviteToken?: string
  invoiceLicenseCompanyName?: string | null
  membershipFee: MembershipFee2
  csvImportSetting: CsvImportSetting
  representatives: CompanyRepresentative[]
  representative?: CompanyRepresentative
  branchOffices: BranchOffice[]
  projectDetail?: ProjectDetail
  visitorRegistrationForm?: VisitorRegistrationForm
  visitorRegistrationFormWorkers: VisitorRegistrationFormWorker[]
  visitorRegistrationFormSubcontractors: VisitorRegistrationFormSubcontractor[]
  visitorRegistrationRecordSet: VisitorRegistrationRecordSet
  agreementSettings?: CompanyAgreementSettings
}

export const initialCompanyState: CompanyState = {
  company: undefined,
  clients: [],
  users: [],
  bankSearchResults: [],
  bankBranchSearchResults: [],
  membershipFee: {
    name: '協力会費',
    taxIn: 1,
    type: 1,
    conditions: [],
    maximumFee: null,
    roundingType: 1,
  },
  csvImportSetting: {
    bundlingKeyField: 'name',
    externalId: '仕入先コード',
    name: '取引名',
    isTaxIn: '消費税の表示',
    publishedAt: '発行日',
    note: '備考',
    paymentDate: '支払日',
    closingDate: '締め日',
    deliveryDateFrom: '納入期間開始日',
    deliveryDateTo: '納入期間終了日',
    picId: '担当者1ID',
    picSubId: '担当者2ID',
    siteAddress: '現場住所',
    customProp1: 'カスタム項目1',
    customProp2: 'カスタム項目2',
    customProp3: 'カスタム項目3',
    customProp4: 'カスタム項目4',
    detailDefaultProp: '明細デフォルト項目',
    detailCustomProp1: '明細カスタム項目1',
    detailCustomProp2: '明細カスタム項目2',
    detailUnitPrice: '単価',
    detailQuantity: '数量',
    detailUnit: '単位',
    detailTaxBucketId: '税区分',
    skippingKeyField: null,
    skippingTargetValues: [{ value: '' }],
  },
  representatives: [],
  branchOffices: [],
  visitorRegistrationFormWorkers: [],
  visitorRegistrationFormSubcontractors: [],
  visitorRegistrationRecordSet: {
    submittedRecords: [],
    receivedRecords: [],
  },
}

export const CompanyActions = {
  fetchCompany: createAsyncAction<
    void,
    {
      company?: Company
      inviteToken?: string
      membershipFee?: MembershipFee2
      csvImportSetting?: CsvImportSetting | null
    }
  >('fetchCompany', async (params, { companyRepository }, state) => {
    if (state.session.companyId) {
      const myCompany = await companyRepository.loadCompany(state.session.companyId)
      const inviteToken = await companyRepository.inviteToken(myCompany.id)
      const membershipFee = convertMembershipFee(myCompany.membershipFee)
      const csvImportSetting = await companyRepository.loadCsvImportSetting(myCompany.id)
      return { company: myCompany, inviteToken, membershipFee, csvImportSetting }
    }
    return { company: undefined, inviteToken: undefined, membershipFee: undefined, csvImportSetting: undefined }
  }),
  saveCompany: createAsyncAction<
    {
      id: string
      company: CompanyAttributes
      representativeUserId?: string | null
    },
    {
      company: Company
      moneyspaces: Moneyspace[]
    }
  >('saveCompany', async (params, { companyRepository, moneyspaceRepository }, state) => {
    if (params.representativeUserId && state.company.company?.representative == null) {
      await companyRepository.postRepresentative(params.id, params.representativeUserId)
    }
    const company = await companyRepository.saveCompany(params.id, params.company)
    const moneyspaces = await moneyspaceRepository.loadMoneyspaces()
    return { company, moneyspaces }
  }),
  saveTransactionConfig: createAsyncAction<
    {
      companyId: string
      detailPropConfig: DetailPropConfig
      taxRoundingType: number
      transactionPropConfig: TransactionPropConfig
    },
    {
      company: Company
    }
  >('saveTransactionConfig', async (params, { companyRepository }) => {
    const company = await companyRepository.saveTransactionConfig(
      params.companyId,
      params.detailPropConfig,
      params.taxRoundingType,
      params.transactionPropConfig
    )
    return { company }
  }),
  saveInvoice: createAsyncAction<
    {
      companyId: string
      invoiceLessActionType: number
    },
    {
      company: Company
    }
  >('saveInvoice', async (params, { companyRepository }) => {
    const company = await companyRepository.saveInvoice(params.companyId, params.invoiceLessActionType)
    return { company }
  }),
  saveCovenant: createAsyncAction<
    {
      covenant: string | null
      onDone: () => void
    },
    void
  >('saveCovenant', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.saveCovenant(companyId, params.covenant)
      params.onDone()
    }
  }),
  fetchClients: createAsyncAction<
    void,
    {
      clients: Company[]
    }
  >('fetchClients', async (params, { companyRepository }, state) => {
    if (state.session.companyId) {
      const clients = await companyRepository.loadClients(state.session.companyId)
      return { clients }
    }
    return { clients: [] }
  }),
  fetchClientDetails: createAsyncAction<
    void,
    {
      clientDetails: ClientDetail[]
    }
  >('fetchClientDetails', async (params, { companyRepository }, state) => {
    if (state.session.companyId) {
      const clientDetails = await companyRepository.loadClientDetails(state.session.companyId, true)
      return { clientDetails }
    }
    return { clientDetails: [] }
  }),
  createClient: createAsyncAction<
    {
      companyUid: string
    },
    {
      clients: Company[]
    }
  >('createClient', async (params, { companyRepository }, state) => {
    if (state.session.companyId) {
      await companyRepository.createClient(state.session.companyId, params.companyUid)
      const clients = await companyRepository.loadClients(state.session.companyId)
      return { clients }
    }
    return { clients: [] }
  }),
  inviteUser: createAsyncAction<
    {
      email: string
    },
    void
  >('inviteUser', async (params, { companyRepository }, state) => {
    if (state.session.companyId) {
      await companyRepository.inviteToken(state.session.companyId, params.email)
    }
  }),
  updateClient: createAsyncAction<
    {
      id: string
      client: CompanyAttributes
    },
    {
      client?: Company
    }
  >('updateClient', async (params, { companyRepository }, state) => {
    if (state.session.companyId) {
      const client = await companyRepository.saveClient(state.session.companyId, params.id, params.client)
      return { client }
    }
    return { client: undefined }
  }),
  deleteClient: createAsyncAction<
    {
      client: Company
    },
    {
      client: Company
    }
  >('deleteClient', async (params, { companyRepository }, state) => {
    if (state.session.companyId) {
      await companyRepository.deleteClient(state.session.companyId, params.client)
    }
    return { client: params.client }
  }),
  acceptClient: createAsyncAction<
    {
      clientId: string
    },
    {
      clientId: string
    }
  >('acceptClient', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.acceptClient(companyId, params.clientId)
    }
    return {
      clientId: params.clientId,
    }
  }),
  fetchUsers: createAsyncAction<
    void,
    {
      users: User[]
    }
  >('fetchUsers', async (params, { userRepository }, state) => {
    const users = await userRepository.loadUsers()
    const companyId = state.session.companyId ?? ''
    return { users: users.map((user) => ({ ...user, companyId })) }
  }),
  createUser: createAsyncAction<
    {
      name: string
      email: string
      password: string
      role: Role
    },
    {
      user: User
    }
  >('createUser', async (params, { userRepository }) => {
    const newUser = await userRepository.createUser(params.name, params.email, params.password, params.role)
    return { user: newUser }
  }),
  updateUser: createAsyncAction<
    {
      id: string
      name?: string
      email?: string
      role?: Role
    },
    {
      user: User
    }
  >('updateUser', async (params, { userRepository }) => {
    const updatedUser = await userRepository.saveUser(params.id, params.name, params.email, params.role)
    return { user: updatedUser }
  }),
  deleteUser: createAsyncAction<
    {
      user: User
    },
    {
      user: User
      approvalFlows: ApprovalFlow[]
    }
  >('deleteUser', async (params, { userRepository, companyRepository }) => {
    await userRepository.deleteUser(params.user)
    // ユーザーを削除したことにより、承認フローが更新されている可能性があるため、再fetchする
    const approvalFlows = params.user.companyId ? await companyRepository.loadApprovalFlows(params.user.companyId) : []
    return { user: params.user, approvalFlows }
  }),
  searchBanks: createAsyncAction<
    {
      code?: string
      name?: string
    },
    {
      searchResults: Bank[]
    }
  >('searchBanks', async (params, { bankRepository }) => {
    const searchResults = await bankRepository.searchBanks({ code: params.code, name: params.name })
    return { searchResults }
  }),
  searchBankBranches: createAsyncAction<
    {
      bankCode: string
      code?: string
      name?: string
    },
    {
      searchResults: BankBranch[]
    }
  >('searchBankBranches', async (params, { bankRepository }) => {
    const searchResults = await bankRepository.searchBankBranches(params.bankCode, {
      code: params.code,
      name: params.name,
    })
    return { searchResults }
  }),
  searchInvoiceLicense: createAsyncAction<
    {
      invoiceLicenseNumber?: string
    },
    {
      companyName?: string | null
    }
  >('searchInvoiceLicense', async (params, { companyRepository }) => {
    if (params.invoiceLicenseNumber) {
      const companyName = await companyRepository.searchInvoiceLicense(params.invoiceLicenseNumber)
      return {
        companyName,
      }
    }
    return {}
  }),
  saveMembershipFee: createAsyncAction<
    {
      membershipFee: MembershipFee2
    },
    {
      membershipFee: MembershipFee2
    }
  >('saveMembershipFee', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.saveMembershipFee(companyId, params.membershipFee)
    }
    return { membershipFee: params.membershipFee }
  }),
  saveAggregateTransaction: createAsyncAction<AggregateTransaction, AggregateTransaction>(
    'saveAggregateTransaction',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        await companyRepository.saveAggregateTransaction(
          companyId,
          params.aggregateTransactions,
          params.fixMonth,
          params.fixDay
        )
      }
      return params
    }
  ),
  saveCsvImportSetting: createAsyncAction<CsvImportSetting, CsvImportSetting>(
    'saveCsvImportSetting',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        await companyRepository.saveCsvImportSetting(companyId, params)
      }
      return params
    }
  ),
  saveWorkAreas: createAsyncAction<{ areaIds: string[] }, { areaIds: string[] }>(
    'saveWorkAreas',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        await companyRepository.saveWorkAreas(companyId, params.areaIds)
      }
      return params
    }
  ),
  saveConstructions: createAsyncAction<
    { constructions: { typeId: string; detail?: string }[] },
    { constructions: { typeId: string; detail?: string }[] }
  >('saveConstructions', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.saveConstructions(companyId, params.constructions)
    }
    return params
  }),
  downloadClientCsv: createAsyncAction<
    {
      filename: string
      searchParamName?: string
      searchParamInvoice?: number
    },
    void
  >('company:downloadClientCsv', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.downloadClientCsv(
        params.filename,
        companyId,
        params.searchParamName,
        params.searchParamInvoice
      )
    }
  }),
  saveClientEvaluation: createAsyncAction<
    {
      clientCompanyId: string
      communicationSpeedPoint: number
      irregularitiesResponsePoint: number
      budgetPoint: number
      constructionSchedulePoint: number
      technologyPoint: number
      comment: string | null
    },
    {
      clientId: string
      evaluation: CompanyEvaluation | null
    }
  >('company:saveClientEvaluation', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.saveClientEvaluation(
        companyId,
        params.clientCompanyId,
        params.communicationSpeedPoint,
        params.irregularitiesResponsePoint,
        params.budgetPoint,
        params.constructionSchedulePoint,
        params.technologyPoint,
        params.comment
      )
      const evaluation = await companyRepository.loadClientEvaluation(companyId, params.clientCompanyId, true)
      return { clientId: params.clientCompanyId, evaluation }
    }
    return { clientId: params.clientCompanyId, evaluation: null }
  }),
  saveClientEvaluationComment: createAsyncAction<
    {
      clientCompanyId: string
      commentId: string
      comment: string
    },
    {
      clientId: string
      evaluation: CompanyEvaluation | null
    }
  >('company:saveClientEvaluationComment', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.saveClientEvaluationComment(
        companyId,
        params.clientCompanyId,
        params.commentId,
        params.comment
      )
      const evaluation = await companyRepository.loadClientEvaluation(companyId, params.clientCompanyId, true)
      return { clientId: params.clientCompanyId, evaluation }
    }
    return { clientId: params.clientCompanyId, evaluation: null }
  }),
  deleteClientEvaluationComment: createAsyncAction<
    {
      clientCompanyId: string
      commentId: string
    },
    {
      clientId: string
      commentId: string
    }
  >('company:deleteClientEvaluationComment', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.deleteClientEvaluationComment(companyId, params.clientCompanyId, params.commentId)
    }
    return { clientId: params.clientCompanyId, commentId: params.commentId }
  }),
  saveRepresentative: createAsyncAction<{ userId: string; message?: boolean }, { company?: Company }>(
    'company:saveRepresentative',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        await companyRepository.postRepresentative(companyId, params.userId, params.message)
        const company = await companyRepository.loadCompany(companyId)
        return { company }
      }
      return {}
    }
  ),
  uploadRepresentativeCertification: createAsyncAction<
    { files: File[]; redirectToCompleted?: boolean },
    { company?: Company; redirectTo?: string }
  >('company:uploadRepresentativeCertification', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.uploadRepresentativeCertification(companyId, params.files)
      const company = await companyRepository.loadCompany(companyId)
      return {
        company,
        redirectTo: params.redirectToCompleted ? '/settings/company/representative/completed' : undefined,
      }
    }
    return {}
  }),
  fetchRepresentatives: createAsyncAction<void, { representatives: CompanyRepresentative[] }>(
    'company:fetchRepresentatives',
    async (params, { companyRepository }) => {
      const representatives = await companyRepository.loadRepresentatives()
      return { representatives }
    }
  ),
  fetchRepresentative: createAsyncAction<{ id: string }, { representative?: CompanyRepresentative }>(
    'company:fetchRepresentative',
    async (params, { companyRepository }) => {
      const representative = await companyRepository.loadRepresentative(params.id)
      return { representative }
    }
  ),
  acceptRepresentative: createAsyncAction<
    { companyId: string; representativeId: string },
    { representative?: CompanyRepresentative }
  >('company:acceptRepresentative', async (params, { companyRepository }) => {
    await companyRepository.acceptRepresentative(params.companyId)
    const representative = await companyRepository.loadRepresentative(params.representativeId)
    return { representative }
  }),
  rejectRepresentative: createAsyncAction<
    { companyId: string; representativeId: string; reason: string },
    { representative?: CompanyRepresentative }
  >('company:rejectRepresentative', async (params, { companyRepository }) => {
    await companyRepository.rejectRepresentative(params.companyId, params.reason)
    const representative = await companyRepository.loadRepresentative(params.representativeId)
    return { representative }
  }),
  fetchBranchOffices: createAsyncAction<void, { branchOffices: BranchOffice[] }>(
    'company:fetchBranchOffices',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        const branchOffices = await companyRepository.loadBranchOffices(companyId)
        return { branchOffices }
      }
      return { branchOffices: [] }
    }
  ),
  createBranchOffice: createAsyncAction<{ name: string }, { branchOffice?: BranchOffice }>(
    'company:createBranchOffice',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        const branchOffice = await companyRepository.createBranchOffice(companyId, params.name)
        return { branchOffice }
      }
      return {}
    }
  ),
  updateBranchOffice: createAsyncAction<{ branchOfficeId: string; name: string }, { branchOffice?: BranchOffice }>(
    'company:updateBranchOffice',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        const branchOffice = await companyRepository.updateBranchOffice(companyId, params.branchOfficeId, params.name)
        return { branchOffice }
      }
      return {}
    }
  ),
  deleteBranchOffice: createAsyncAction<{ branchOfficeId: string }, { branchOfficeId?: string }>(
    'company:deleteBranchOffice',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        await companyRepository.deleteBranchOffice(companyId, params.branchOfficeId)
        return { branchOfficeId: params.branchOfficeId }
      }
      return {}
    }
  ),
  addBranchOfficeMember: createAsyncAction<
    { branchOfficeId: string; userId: string },
    { branchOfficeId?: string; userId?: string }
  >('company:addBranchOfficeMember', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.addBranchOfficeMember(companyId, params.branchOfficeId, params.userId)
      return { branchOfficeId: params.branchOfficeId, userId: params.userId }
    }
    return {}
  }),
  removeBranchOfficeMember: createAsyncAction<
    { branchOfficeId: string; userId: string },
    { branchOfficeId?: string; userId?: string }
  >('company:removeBranchOfficeMember', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.removeBranchOfficeMember(companyId, params.branchOfficeId, params.userId)
      return { branchOfficeId: params.branchOfficeId, userId: params.userId }
    }
    return {}
  }),
  fetchProjectDetail: createAsyncAction<{ projectId: string }, { projectDetail?: ProjectDetail }>(
    'company:fetchProjectDetail',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        const detail = await companyRepository.loadProjectDetail(companyId, params.projectId)
        return {
          projectDetail: detail,
        }
      }
      return {}
    }
  ),
  fetchVisitorRegistrationForm: createAsyncAction<void, { form?: VisitorRegistrationForm }>(
    'company:fetchVisitorRegistrationForm',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        const form = await companyRepository.loadVisitorRegistrationForm(companyId)
        return { form }
      }
      return {}
    }
  ),
  saveVisitorRegistrationForm: createAsyncAction<
    { company: VisitorRegistrationCompanyFields },
    { form?: VisitorRegistrationForm }
  >('company:saveVisitorRegistrationForm', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      const form = await companyRepository.saveVisitorRegistrationForm(companyId, params.company)
      return { form }
    }
    return {}
  }),
  saveVisitorRegistrationAnswers: createAsyncAction<
    { answers: VisitorRegistrationAnswers },
    { form?: VisitorRegistrationForm }
  >('company:saveVisitorRegistrationAnswers', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      const form = await companyRepository.saveVisitorRegistrationAnswers(companyId, params.answers)
      return { form }
    }
    return {}
  }),
  fetchVisitorRegistrationFormWorkers: createAsyncAction<void, { workers: VisitorRegistrationFormWorker[] }>(
    'company:fetchVisitorRegistrationFormWorkers',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        const workers = await companyRepository.loadVisitorRegistrationFormWorkers(companyId)
        return { workers }
      }
      return { workers: [] }
    }
  ),
  createVisitorRegistrationFormWorker: createAsyncAction<
    { fields: VisitorRegistrationFormWorkerFields },
    { workers: VisitorRegistrationFormWorker[] }
  >('company:createVisitorRegistrationFormWorker', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.createVisitorRegistrationFormWorker(companyId, params.fields)
      const workers = await companyRepository.loadVisitorRegistrationFormWorkers(companyId)
      return { workers }
    }
    return { workers: [] }
  }),
  updateVisitorRegistrationFormWorker: createAsyncAction<
    { workerId: string; fields: VisitorRegistrationFormWorkerFields },
    { workers: VisitorRegistrationFormWorker[] }
  >('company:updateVisitorRegistrationFormWorker', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.updateVisitorRegistrationFormWorker(companyId, params.workerId, params.fields)
      const workers = await companyRepository.loadVisitorRegistrationFormWorkers(companyId)
      return { workers }
    }
    return { workers: [] }
  }),
  deleteVisitorRegistrationFormWorker: createAsyncAction<
    { workerId: string },
    { workers: VisitorRegistrationFormWorker[] }
  >('company:deleteVisitorRegistrationFormWorker', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.deleteVisitorRegistrationFormWorker(companyId, params.workerId)
      const workers = await companyRepository.loadVisitorRegistrationFormWorkers(companyId)
      return { workers }
    }
    return { workers: [] }
  }),
  fetchVisitorRegistrationFormSubcontractors: createAsyncAction<
    void,
    { subcontractors: VisitorRegistrationFormSubcontractor[] }
  >('company:fetchVisitorRegistrationFormSubcontractors', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      const subcontractors = await companyRepository.loadVisitorRegistrationFormSubcontractors(companyId)
      return { subcontractors }
    }
    return { subcontractors: [] }
  }),
  createVisitorRegistrationFormSubcontractor: createAsyncAction<
    { fields: VisitorRegistrationFormSubcontractorFields },
    { subcontractors: VisitorRegistrationFormSubcontractor[] }
  >('company:createVisitorRegistrationFormSubcontractor', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.createVisitorRegistrationFormSubcontractor(companyId, params.fields)
      const subcontractors = await companyRepository.loadVisitorRegistrationFormSubcontractors(companyId)
      return { subcontractors }
    }
    return { subcontractors: [] }
  }),
  updateVisitorRegistrationFormSubcontractor: createAsyncAction<
    { subcontractorId: string; fields: VisitorRegistrationFormSubcontractorFields },
    { subcontractors: VisitorRegistrationFormSubcontractor[] }
  >('company:updateVisitorRegistrationFormSubcontractor', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.updateVisitorRegistrationFormSubcontractor(
        companyId,
        params.subcontractorId,
        params.fields
      )
      const subcontractors = await companyRepository.loadVisitorRegistrationFormSubcontractors(companyId)
      return { subcontractors }
    }
    return { subcontractors: [] }
  }),
  deleteVisitorRegistrationFormSubcontractor: createAsyncAction<
    { subcontractorId: string },
    { subcontractors: VisitorRegistrationFormSubcontractor[] }
  >('company:deleteVisitorRegistrationFormSubcontractor', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.deleteVisitorRegistrationFormSubcontractor(companyId, params.subcontractorId)
      const subcontractors = await companyRepository.loadVisitorRegistrationFormSubcontractors(companyId)
      return { subcontractors }
    }
    return { subcontractors: [] }
  }),
  fetchVisitorRegistrationRecords: createAsyncAction<void, { recordSet: VisitorRegistrationRecordSet }>(
    'company:fetchVisitorRegistrationRecords',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        const recordSet = await companyRepository.loadVisitorRegistrationRecords(companyId)
        return { recordSet }
      }
      return { recordSet: { submittedRecords: [], receivedRecords: [] } }
    }
  ),
  createVisitorRegistrationRecord: createAsyncAction<
    { clientId: string; workerIds: string[]; subcontractorIds: string[] },
    { recordSet: VisitorRegistrationRecordSet }
  >('company:submitVisitorRegistrationForm', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      await companyRepository.createVisitorRegistrationRecord(
        companyId,
        params.clientId,
        params.workerIds,
        params.subcontractorIds
      )
      const recordSet = await companyRepository.loadVisitorRegistrationRecords(companyId)
      return { recordSet }
    }
    return { recordSet: { submittedRecords: [], receivedRecords: [] } }
  }),
  fetchAgreementSettings: createAsyncAction<void, { settings?: CompanyAgreementSettings }>(
    'company:fetchAgreementSettings',
    async (params, { companyRepository }, state) => {
      const { companyId } = state.session
      if (companyId) {
        const settings = await companyRepository.loadAgreementSettings(companyId)
        return { settings }
      }
      return {}
    }
  ),
  saveAgreementSettings: createAsyncAction<
    { users: RelatedUser[]; onSuccess?: () => void },
    { settings?: CompanyAgreementSettings }
  >('company:saveAgreementSettings', async (params, { companyRepository }, state) => {
    const { companyId } = state.session
    if (companyId) {
      const settings = await companyRepository.updateAgreementSettings(
        companyId,
        params.users.map((user) => user.id)
      )
      if (params.onSuccess) {
        params.onSuccess()
      }
      return { settings }
    }
    return {}
  }),
}

const companySlice = createSlice({
  name: 'company',
  initialState: initialCompanyState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(CompanyActions.fetchClients.fulfilled, (state, action) => {
        state.clients = action.payload.clients
      })
      .addCase(CompanyActions.fetchClientDetails.fulfilled, (state, action) => {
        state.clientDetails = action.payload.clientDetails
      })
      .addCase(CompanyActions.fetchUsers.fulfilled, (state, action) => {
        state.users = action.payload.users
      })
      .addCase(CompanyActions.fetchCompany.fulfilled, (state, action) => {
        state.company = action.payload.company
        state.inviteToken = action.payload.inviteToken
        if (action.payload.membershipFee) {
          state.membershipFee = action.payload.membershipFee
        }
        if (action.payload.csvImportSetting) {
          state.csvImportSetting = action.payload.csvImportSetting
        }
      })
      .addCase(CompanyActions.saveCompany.fulfilled, (state, action) => {
        state.company = action.payload.company
      })
      .addCase(CompanyActions.saveTransactionConfig.fulfilled, (state, action) => {
        state.company = action.payload.company
      })
      .addCase(CompanyActions.saveInvoice.fulfilled, (state, action) => {
        state.company = action.payload.company
      })
      .addCase(CompanyActions.createUser.fulfilled, (state, action) => {
        state.users.unshift(action.payload.user)
      })
      .addCase(CompanyActions.updateUser.fulfilled, (state, action) => {
        state.users = state.users.map((user) => {
          if (user.id === action.payload.user.id) {
            return action.payload.user
          }
          return user
        })
      })
      .addCase(CompanyActions.deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload.user.id)
      })
      .addCase(CompanyActions.createClient.fulfilled, (state, action) => {
        state.clients = action.payload.clients
      })
      .addCase(CompanyActions.updateClient.fulfilled, (state, action) => {
        const { client } = action.payload
        if (client) {
          state.clients = state.clients.map((item) => {
            if (item.id === client.id) {
              return client
            }
            return item
          })
        }
      })
      .addCase(CompanyActions.deleteClient.fulfilled, (state, action) => {
        state.clients = state.clients.filter((client) => client.id !== action.payload.client.id)
      })
      .addCase(CompanyActions.acceptClient.fulfilled, (state, action) => {
        const client = state.clients.find((item) => item.id === action.payload.clientId)
        if (client) {
          client.isAccepted = true
        }
      })
      .addCase(CompanyActions.searchBanks.fulfilled, (state, action) => {
        state.bankSearchResults = action.payload.searchResults
      })
      .addCase(CompanyActions.searchBankBranches.fulfilled, (state, action) => {
        state.bankBranchSearchResults = action.payload.searchResults
      })
      .addCase(CompanyActions.searchInvoiceLicense.pending, (state) => {
        state.invoiceLicenseCompanyName = undefined
      })
      .addCase(CompanyActions.searchInvoiceLicense.fulfilled, (state, action) => {
        state.invoiceLicenseCompanyName = action.payload.companyName
      })
      .addCase(CompanyActions.saveMembershipFee.fulfilled, (state, action) => {
        state.membershipFee = action.payload.membershipFee
      })
      .addCase(CompanyActions.saveAggregateTransaction.fulfilled, (state, action) => {
        if (state.company) {
          state.company.aggregateTransactions = action.payload.aggregateTransactions
          state.company.fixMonth = action.payload.fixMonth
          state.company.fixDay = action.payload.fixDay
        }
      })
      .addCase(CompanyActions.saveCsvImportSetting.fulfilled, (state, action) => {
        state.csvImportSetting = action.payload
      })
      .addCase(JobActions.createJob.fulfilled, (state, action) => {
        if (state.company) {
          state.company.jobId = action.payload.job?.id
        }
      })
      .addCase(CompanyActions.saveClientEvaluation.fulfilled, (state, action) => {
        state.clientDetails = state.clientDetails?.map((detail) => {
          if (detail.id === action.payload.clientId) {
            return {
              ...detail,
              evaluation: action.payload.evaluation,
            }
          }
          return detail
        })
      })
      .addCase(CompanyActions.saveClientEvaluationComment.fulfilled, (state, action) => {
        state.clientDetails = state.clientDetails?.map((detail) => {
          if (detail.id === action.payload.clientId) {
            return {
              ...detail,
              evaluation: action.payload.evaluation,
            }
          }
          return detail
        })
      })
      .addCase(CompanyActions.deleteClientEvaluationComment.fulfilled, (state, action) => {
        state.clientDetails = state.clientDetails?.map((detail) => {
          if (detail.id === action.payload.clientId) {
            return {
              ...detail,
              evaluation: detail.evaluation
                ? {
                    ...detail.evaluation,
                    comments: detail.evaluation.comments.filter((comment) => comment.id !== action.payload.commentId),
                  }
                : null,
            }
          }
          return detail
        })
      })
      .addCase(CompanyActions.saveRepresentative.fulfilled, (state, action) => {
        if (action.payload.company) {
          state.company = action.payload.company
        }
      })
      .addCase(CompanyActions.uploadRepresentativeCertification.fulfilled, (state, action) => {
        if (action.payload.company) {
          state.company = action.payload.company
        }
      })
      .addCase(CompanyActions.fetchRepresentatives.fulfilled, (state, action) => {
        state.representatives = action.payload.representatives
      })
      .addCase(CompanyActions.fetchRepresentative.pending, (state) => {
        state.representative = undefined
      })
      .addCase(CompanyActions.fetchRepresentative.fulfilled, (state, action) => {
        state.representative = action.payload.representative
      })
      .addCase(CompanyActions.acceptRepresentative.fulfilled, (state, action) => {
        state.representative = action.payload.representative
      })
      .addCase(CompanyActions.rejectRepresentative.fulfilled, (state, action) => {
        state.representative = action.payload.representative
      })
      .addCase(DashboardActions.fetchDashboard.fulfilled, (state, action) => {
        state.representatives = action.payload.representatives
      })
      .addCase(CompanyActions.fetchBranchOffices.fulfilled, (state, action) => {
        state.branchOffices = action.payload.branchOffices
      })
      .addCase(CompanyActions.createBranchOffice.fulfilled, (state, action) => {
        if (action.payload.branchOffice) {
          state.branchOffices = [action.payload.branchOffice, ...state.branchOffices]
        }
      })
      .addCase(CompanyActions.updateBranchOffice.fulfilled, (state, action) => {
        state.branchOffices = state.branchOffices.map((branchOffice) => {
          if (action.payload.branchOffice && branchOffice.id === action.payload.branchOffice.id) {
            return action.payload.branchOffice
          }
          return branchOffice
        })
      })
      .addCase(CompanyActions.deleteBranchOffice.fulfilled, (state, action) => {
        state.branchOffices = state.branchOffices.filter(
          (branchOffice) => branchOffice.id !== action.payload.branchOfficeId
        )
      })
      .addCase(CompanyActions.addBranchOfficeMember.fulfilled, (state, action) => {
        state.branchOffices = state.branchOffices.map((branchOffice) => {
          if (branchOffice.id === action.payload.branchOfficeId && action.payload.userId) {
            return {
              ...branchOffice,
              members: [action.payload.userId, ...branchOffice.members],
            }
          }
          return branchOffice
        })
      })
      .addCase(CompanyActions.removeBranchOfficeMember.fulfilled, (state, action) => {
        state.branchOffices = state.branchOffices.map((branchOffice) => {
          if (branchOffice.id === action.payload.branchOfficeId && action.payload.userId) {
            return {
              ...branchOffice,
              members: branchOffice.members.filter((userId) => userId !== action.payload.userId),
            }
          }
          return branchOffice
        })
      })
      .addCase(CompanyActions.fetchProjectDetail.fulfilled, (state, action) => {
        state.projectDetail = action.payload.projectDetail
      })
      .addCase(TransactionActions.createTransaction.fulfilled, (state, action) => {
        state.projectDetail = action.payload.projectDetail
      })
      .addCase(CompanyActions.fetchVisitorRegistrationForm.fulfilled, (state, action) => {
        state.visitorRegistrationForm = action.payload.form
      })
      .addCase(CompanyActions.saveVisitorRegistrationAnswers.fulfilled, (state, action) => {
        state.visitorRegistrationForm = action.payload.form
      })
      .addCase(CompanyActions.fetchVisitorRegistrationFormWorkers.fulfilled, (state, action) => {
        state.visitorRegistrationFormWorkers = action.payload.workers
      })
      .addCase(CompanyActions.createVisitorRegistrationFormWorker.fulfilled, (state, action) => {
        state.visitorRegistrationFormWorkers = action.payload.workers
      })
      .addCase(CompanyActions.updateVisitorRegistrationFormWorker.fulfilled, (state, action) => {
        state.visitorRegistrationFormWorkers = action.payload.workers
      })
      .addCase(CompanyActions.deleteVisitorRegistrationFormWorker.fulfilled, (state, action) => {
        state.visitorRegistrationFormWorkers = action.payload.workers
      })
      .addCase(CompanyActions.fetchVisitorRegistrationFormSubcontractors.fulfilled, (state, action) => {
        state.visitorRegistrationFormSubcontractors = action.payload.subcontractors
      })
      .addCase(CompanyActions.createVisitorRegistrationFormSubcontractor.fulfilled, (state, action) => {
        state.visitorRegistrationFormSubcontractors = action.payload.subcontractors
      })
      .addCase(CompanyActions.updateVisitorRegistrationFormSubcontractor.fulfilled, (state, action) => {
        state.visitorRegistrationFormSubcontractors = action.payload.subcontractors
      })
      .addCase(CompanyActions.deleteVisitorRegistrationFormSubcontractor.fulfilled, (state, action) => {
        state.visitorRegistrationFormSubcontractors = action.payload.subcontractors
      })
      .addCase(CompanyActions.fetchVisitorRegistrationRecords.fulfilled, (state, action) => {
        state.visitorRegistrationRecordSet = action.payload.recordSet
      })
      .addCase(CompanyActions.createVisitorRegistrationRecord.fulfilled, (state, action) => {
        state.visitorRegistrationRecordSet = action.payload.recordSet
      })
      .addCase(CompanyActions.fetchAgreementSettings.fulfilled, (state, action) => {
        state.agreementSettings = action.payload.settings
      })
      .addCase(CompanyActions.saveAgreementSettings.fulfilled, (state, action) => {
        state.agreementSettings = action.payload.settings
      })
  },
})

export default companySlice
